import { frameTransform, vectorPathMatrix, productionPage } from "@proof/core";
import { useDocumentScene, type FontLoader } from "./fonts";
import {
  useId,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type PointerEvent,
} from "react";
import QRCode from "qrcode";
import {
  fontCssFamily,
  movementLayoutGroup,
  layoutGroupMemberIds,
  textLines,
  textPaintRuns,
  textFeatureCss,
  type Project,
  type Values,
  type SceneNode,
  type ScenePage,
  type DesignNode,
  type OutputProfile,
  type Context,
} from "@proof/core";
import { ImageLayer, type ImagePreviewStatus } from "./ImageLayer";
import {
  defaultAssetUrl,
  imagePreviewKey,
  type ImageTransformer,
  type AssetLoader,
} from "./images";
export interface ProofCanvasProps {
  loadAsset?: AssetLoader;
  transformImage?: ImageTransformer;
  project: Project;
  values: Values;
  partId?: string;
  pageIndex?: number;
  sourcePageId?: string;
  profile?: OutputProfile;
  showProduction?: boolean;
  context?: Context;
  zoom?: number;
  designer?: boolean;
  selectedId?: string;
  onSelect?: (id: string) => void;
  onGroupMove?: (groupId: string, dx: number, dy: number) => void;
  onNodeChange?: (id: string, patch: Partial<DesignNode>) => void;
  assetUrl?: (id: string) => string;
  grid?: boolean;
  onPageCount?: (count: number) => void;
  onLayout?: (page: ScenePage) => void;
  loadFont?: FontLoader;
  onStatus?: (status: { ready: boolean; error?: string }) => void;
}
function Qr({ node }: { node: SceneNode }) {
  const qr = useMemo(() => {
    try {
      return QRCode.create(node.qrPayload || " ", {
        errorCorrectionLevel: "M",
      });
    } catch {
      return null;
    }
  }, [node.qrPayload]);
  if (!qr) return null;
  const size = qr.modules.size + 2;
  const d: string[] = [];
  for (let y = 0; y < qr.modules.size; y++)
    for (let x = 0; x < qr.modules.size; x++)
      if (qr.modules.get(y, x)) d.push(`M${x + 1},${y + 1}h1v1h-1z`);
  return (
    <svg
      width={node.width}
      height={node.height}
      viewBox={`0 0 ${size} ${size}`}
    >
      <rect
        width={size}
        height={size}
        fill={node.qrBackground || "transparent"}
      />
      <path d={d.join("")} fill={node.qrForeground || node.color} />
    </svg>
  );
}
export function ProofCanvas({
  project,
  values,
  partId,
  pageIndex = 0,
  sourcePageId,
  profile,
  showProduction,
  context,
  zoom = 1,
  designer = false,
  selectedId,
  onSelect,
  onNodeChange,
  onGroupMove,
  onLayout,
  assetUrl = defaultAssetUrl,
  transformImage,
  loadAsset,
  grid = false,
  onPageCount,
  loadFont,
  onStatus,
}: ProofCanvasProps) {
  const framePrefix = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const proof = useDocumentScene(
    project,
    values,
    partId,
    context,
    profile,
    loadFont,
    !designer && !!showProduction,
  );
  const scene = proof.scene || {
    pages: project.pages.map((p) => ({
      ...p,
      width: p.width || 612,
      height: p.height || 792,
      nodes: [] as SceneNode[],
      bleed: undefined as
        { x: number; y: number; width: number; height: number } | undefined,
      trim: { x: 0, y: 0, width: p.width || 612, height: p.height || 792 },
    })),
  };
  const logicalPage = sourcePageId
    ? scene.pages.find((p) => p.id === sourcePageId) || scene.pages[0]
    : scene.pages[Math.min(pageIndex, scene.pages.length - 1)];
  const page = useMemo(
    () =>
      designer && showProduction ? productionPage(logicalPage) : logicalPage,
    [logicalPage, designer, showProduction],
  );
  const [retryImages, setRetryImages] = useState(0),
    neededImages = page.nodes.filter((node) => node.assetId),
    imageScope = useMemo(
      () => ({}),
      [assetUrl, transformImage, loadAsset, retryImages],
    );
  const activeImages = useRef({
    scope: imageScope,
    signatures: new Map<string, string>(),
  });
  activeImages.current = {
    scope: imageScope,
    signatures: new Map(
      neededImages.map((node) => [node.id, imagePreviewKey(node)]),
    ),
  };
  const [imageState, setImageState] = useState<{
    scope: object;
    records: Record<string, ImagePreviewStatus & { signature: string }>;
  }>();
  const reportImage = useCallback(
    (id: string, signature: string, status: ImagePreviewStatus) => {
      if (
        activeImages.current.scope !== imageScope ||
        activeImages.current.signatures.get(id) !== signature
      )
        return;
      setImageState((previous) => {
        const records = previous?.scope === imageScope ? previous.records : {};
        const next = { ...status, signature };
        if (JSON.stringify(records[id]) === JSON.stringify(next))
          return previous;
        return {
          scope: imageScope,
          records: {
            ...Object.fromEntries(
              Object.entries(records).filter(([key]) =>
                activeImages.current.signatures.has(key),
              ),
            ),
            [id]: next,
          },
        };
      });
    },
    [imageScope],
  );
  const imageRecords =
      imageState?.scope === imageScope ? imageState.records : {},
    imageError = neededImages.some(
      (node) =>
        imageRecords[node.id]?.signature === imagePreviewKey(node) &&
        imageRecords[node.id]?.error,
    ),
    imagesReady = neededImages.every(
      (node) =>
        imageRecords[node.id]?.signature === imagePreviewKey(node) &&
        imageRecords[node.id]?.ready,
    );
  useEffect(
    () =>
      onStatus?.({
        ready: !!proof.scene && !proof.loading && imagesReady,
        error:
          proof.error || (imageError ? "IMAGE_PREVIEW_UNAVAILABLE" : undefined),
      }),
    [
      onStatus,
      project.id,
      proof.loading,
      proof.error,
      !!proof.scene,
      imagesReady,
      imageError,
    ],
  );
  useEffect(() => {
    if (proof.scene) onPageCount?.(scene.pages.length);
  }, [scene.pages.length, onPageCount, proof.scene]);
  const svg = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (proof.scene && !proof.loading) onLayout?.(logicalPage);
  }, [proof.scene, proof.loading, onLayout, logicalPage]);
  type Drag = {
    groupId?: string;
    memberIds?: Set<string>;
    id: string;
    x: number;
    y: number;
    startX: number;
    startY: number;
    width: number;
    height: number;
    mode: "move" | "resize";
    dx: number;
    dy: number;
    clientX: number;
    clientY: number;
    pointerId: number;
    moved: boolean;
  };
  const [drag, setDrag] = useState<Drag | null>(null);
  const activeDrag = useRef<Drag | null>(null);
  const clearDrag = () => {
    activeDrag.current = null;
    setDrag(null);
  };
  const point = (e: PointerEvent) => {
    const rect = svg.current!.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) * page.width) / rect.width,
      y: ((e.clientY - rect.top) * page.height) / rect.height,
    };
  };
  const begin = (
    e: PointerEvent,
    id: string,
    mode: "move" | "resize" = "move",
  ) => {
    if (!designer || e.button !== 0 || !e.isPrimary) return;
    e.stopPropagation();
    onSelect?.(id);
    const n = logicalPage.nodes.find((n) => n.id === id);
    if (!n || n.locked) return;
    const source =
      project.pages.find(
        (item) => item.id === sourcePageId || item.id === logicalPage.id,
      ) || project.pages[pageIndex];
    const group = source && movementLayoutGroup(source, id);
    if (group && mode === "resize") return;
    const p = point(e);
    e.currentTarget.setPointerCapture(e.pointerId);
    const next: Drag = {
      ...(group && onGroupMove
        ? {
            groupId: group.id,
            memberIds: layoutGroupMemberIds(source, group.id),
          }
        : {}),
      id,
      x: n.x,
      y: n.y,
      startX: p.x,
      startY: p.y,
      width: n.width,
      height: n.height,
      mode,
      dx: 0,
      dy: 0,
      clientX: e.clientX,
      clientY: e.clientY,
      pointerId: e.pointerId,
      moved: false,
    };
    activeDrag.current = next;
    setDrag(next);
  };
  const move = (e: PointerEvent) => {
    const drag = activeDrag.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    // Selection and normal pointer jitter must not edit or detach grouped layers.
    if (
      !drag.moved &&
      Math.hypot(e.clientX - drag.clientX, e.clientY - drag.clientY) < 3
    )
      return;
    const p = point(e);
    const next = {
      ...drag,
      moved: true,
      dx: p.x - drag.startX,
      dy: p.y - drag.startY,
    };
    activeDrag.current = next;
    setDrag(next);
  };
  const end = (e: PointerEvent) => {
    const drag = activeDrag.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    clearDrag();
    if (
      !drag.moved &&
      Math.hypot(e.clientX - drag.clientX, e.clientY - drag.clientY) < 3
    )
      return;
    const p = point(e),
      dx = p.x - drag.startX,
      dy = p.y - drag.startY;
    if (dx === 0 && dy === 0) return;
    const snap = (n: number) =>
      grid ? Math.round(n / 6) * 6 : Math.round(n * 10) / 10;
    if (drag.groupId && onGroupMove) {
      onGroupMove(
        drag.groupId,
        snap(drag.x + dx) - drag.x,
        snap(drag.y + dy) - drag.y,
      );
      return;
    }
    onNodeChange?.(
      drag.id,
      drag.mode === "move"
        ? { x: snap(drag.x + dx), y: snap(drag.y + dy) }
        : {
            width: Math.max(1, snap(drag.width + dx)),
            height: Math.max(1, snap(drag.height + dy)),
          },
    );
  };
  const selected = page.nodes.find(
    (n) => n.id === selectedId || n.sourceId === selectedId,
  );
  const sourceDesignPage =
    project.pages.find(
      (item) => item.id === sourcePageId || item.id === logicalPage.id,
    ) || project.pages[pageIndex];
  const selectedMovementGroup =
    designer && onGroupMove && selected
      ? movementLayoutGroup(sourceDesignPage, selected.sourceId || selected.id)
      : undefined;
  const selectedGroupMembers = selectedMovementGroup
    ? layoutGroupMemberIds(sourceDesignPage, selectedMovementGroup.id)
    : undefined;
  const width = page.width * zoom,
    height = page.height * zoom;
  if (proof.loading)
    return (
      <div className="font-status" role="status">
        Loading verified document fonts -
      </div>
    );
  if (proof.error)
    return (
      <div className="font-status error-text" role="alert">
        Proof unavailable: {proof.error}
      </div>
    );
  return (
    <>
      {imageError && (
        <p role="alert" className="error-text">
          Image preview unavailable.{" "}
          <button
            type="button"
            onClick={() => setRetryImages((value) => value + 1)}
          >
            Retry image previews
          </button>
        </p>
      )}
      {!imageError && !imagesReady && (
        <p role="status">Preparing image previews…</p>
      )}
      <div
        className={`canvas-shell ${page.background === null || page.frame ? "transparent-canvas" : ""}`}
        style={{ width, height }}
      >
        <svg
          ref={svg}
          width={width}
          height={height}
          viewBox={`0 0 ${page.width} ${page.height}`}
          className="proof-canvas"
          role="img"
          aria-label={`${page.name} document preview`}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={clearDrag}
          onLostPointerCapture={clearDrag}
          onPointerDown={() => designer && onSelect?.("")}
        >
          <defs>
            <pattern
              id="design-grid"
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
              x={page.trim.x}
              y={page.trim.y}
            >
              <circle cx="0" cy="0" r=".45" fill="#6a99a5" opacity=".45" />
            </pattern>
          </defs>
          {page.frame && (
            <defs>
              <clipPath id={`${framePrefix}_page_frame_${page.id}`}>
                <path
                  d={page.frame.path}
                  clipRule={page.frame.fillRule}
                  transform={frameTransform({
                    frame: page.frame,
                    ...page.trim,
                    rotation: 0,
                  })}
                />
              </clipPath>
            </defs>
          )}
          {page.background && (
            <rect
              x={(page.bleed ?? page.trim).x}
              y={(page.bleed ?? page.trim).y}
              width={(page.bleed ?? page.trim).width}
              height={(page.bleed ?? page.trim).height}
              fill={page.background}
              clipPath={
                page.frame
                  ? `url(#${framePrefix}_page_frame_${page.id})`
                  : undefined
              }
            />
          )}{" "}
          {designer &&
            project.pages
              .find((p) => p.id === page.id)
              ?.flowRegions.map((region) => (
                <g
                  key={region.id}
                  data-flow-region={region.id}
                  transform={`translate(${page.trim.x} ${page.trim.y})`}
                  pointerEvents="none"
                >
                  <rect
                    x={region.x}
                    y={region.y}
                    width={region.width}
                    height={region.height}
                    fill="none"
                    stroke="#338a87"
                    strokeWidth={0.7}
                    strokeDasharray="4 3"
                  />
                  <text
                    x={region.x}
                    y={Math.max(9, region.y - 4)}
                    fill="#338a87"
                    fontSize={9}
                  >
                    {region.name}
                  </text>
                </g>
              ))}
          {page.nodes.map((original) => {
            const n =
              drag?.id === original.id ||
              drag?.memberIds?.has(original.sourceId || original.id)
                ? {
                    ...original,
                    x: drag.mode === "move" ? original.x + drag.dx : original.x,
                    y: drag.mode === "move" ? original.y + drag.dy : original.y,
                    width:
                      drag.mode === "resize"
                        ? Math.max(1, drag.width + drag.dx)
                        : original.width,
                    height:
                      drag.mode === "resize"
                        ? Math.max(1, drag.height + drag.dy)
                        : original.height,
                  }
                : original;
            const baseFace = project.fontManifest.find(
              (f) => f.id === n.fontFaceId,
            );
            const family = baseFace
              ? fontCssFamily(baseFace)
              : n.fontFamily === "Georgia" ||
                  n.fontFamily === "Libre Baskerville"
                ? "Libre Baskerville"
                : "Inter";
            let content;
            if (n.type === "path")
              content = (
                <path
                  d={n.path}
                  transform={
                    n.pathViewport
                      ? `matrix(${vectorPathMatrix(n).join(" ")})`
                      : `scale(${n.width / (n.pathWidth || n.width)} ${n.height / (n.pathHeight || n.height)})`
                  }
                  fill={n.fill}
                  stroke={n.stroke}
                  strokeWidth={n.strokeWidth}
                  fillRule={n.fillRule}
                />
              );
            else if (n.type === "rect")
              content = (
                <rect
                  width={n.width}
                  height={n.height}
                  rx={n.radius}
                  fill={n.fill}
                  stroke={n.stroke}
                  strokeWidth={n.strokeWidth}
                />
              );
            else if (n.type === "ellipse")
              content = (
                <ellipse
                  cx={n.width / 2}
                  cy={n.height / 2}
                  rx={n.width / 2}
                  ry={n.height / 2}
                  fill={n.fill}
                  stroke={n.stroke}
                  strokeWidth={n.strokeWidth}
                />
              );
            else if (n.type === "line")
              content = (
                <line
                  x2={n.width}
                  y2={n.height}
                  stroke={n.stroke}
                  strokeWidth={n.strokeWidth}
                />
              );
            else if (n.type === "qr") content = <Qr node={n} />;
            else if (n.type === "image" || n.type === "map")
              content = (
                <ImageLayer
                  loadAsset={loadAsset}
                  node={n}
                  assetUrl={assetUrl}
                  transformImage={transformImage}
                  revision={imageScope}
                  onStatus={reportImage}
                />
              );
            else if (
              n.runs?.some((run) => run.textFeatures?.pairAdjustments?.length)
            ) {
              const paths = textPaintRuns(n).map((run, index) => {
                const faceId = run.fontFaceId || n.fontFaceId;
                const runtime = faceId ? proof.fonts?.get(faceId) : undefined;
                if (!runtime?.outline)
                  throw new Error(
                    "Custom kerning preview requires managed font outlines",
                  );
                const size = run.size || n.fontSize,
                  color = run.color || n.color;
                return (
                  <g key={index}>
                    {runtime
                      .outline(run.text, run.textFeatures)
                      .map((glyph, i) => (
                        <path
                          key={i}
                          d={glyph.path}
                          transform={`translate(${run.x + glyph.x * size} ${run.y - glyph.y * size}) scale(${glyph.scale * size} ${-glyph.scale * size})`}
                          fill={color}
                        />
                      ))}
                    {run.underline && (
                      <line
                        x1={run.x}
                        x2={run.x + run.width}
                        y1={run.y - (runtime.underlinePosition || -0.1) * size}
                        y2={run.y - (runtime.underlinePosition || -0.1) * size}
                        stroke={color}
                        strokeWidth={Math.max(
                          0.1,
                          (runtime.underlineThickness || 0.05) * size,
                        )}
                      />
                    )}
                  </g>
                );
              });
              content = (
                <>
                  <clipPath id={`preview_clip_${n.id}`}>
                    <rect width={n.width} height={n.height} />
                  </clipPath>
                  <g
                    className="pair-adjusted-text"
                    role="img"
                    aria-label={n.text}
                    clipPath={
                      n.allowGlyphOverhang
                        ? undefined
                        : `url(#preview_clip_${n.id})`
                    }
                  >
                    <title>{n.text}</title>
                    {paths}
                  </g>
                </>
              );
            } else {
              content = (
                <>
                  <clipPath id={`preview_clip_${n.id}`}>
                    <rect width={n.width} height={n.height} />
                  </clipPath>
                  <text
                    clipPath={
                      n.allowGlyphOverhang
                        ? undefined
                        : `url(#preview_clip_${n.id})`
                    }
                    fill={n.color}
                    fontFamily={family}
                    fontSize={n.fontSize}
                    fontWeight={n.fontWeight}
                    textAnchor="start"
                    style={{ fontSynthesis: "none" }}
                  >
                    {textPaintRuns(n).map((run, i) => (
                      <tspan
                        key={i}
                        x={run.x}
                        y={run.y}
                        fill={run.color || n.color}
                        fontSize={run.size || n.fontSize}
                        fontFamily={
                          run.fontFaceId ? `Proof_${run.fontFaceId}` : family
                        }
                        fontWeight={
                          project.fontManifest.find(
                            (f) => f.id === run.fontFaceId,
                          )?.weight || (run.bold ? 700 : n.fontWeight)
                        }
                        fontStyle={
                          project.fontManifest.find(
                            (f) => f.id === run.fontFaceId,
                          )?.style || (run.italic ? "italic" : "normal")
                        }
                        textDecoration={run.underline ? "underline" : "none"}
                        style={{
                          fontFeatureSettings: textFeatureCss(run.textFeatures),
                        }}
                      >
                        {run.text}
                      </tspan>
                    ))}
                  </text>
                </>
              );
            }
            if (n.imagePlacement) {
              const t = n.imagePlacement;
              content = (
                <g
                  transform={`matrix(${t.scaleX} 0 0 ${t.scaleY} ${t.offsetX} ${t.offsetY})`}
                >
                  {content}
                </g>
              );
            }
            if (n.clipImageToFrame && (n.type === "image" || n.type === "map"))
              content = (
                <>
                  <clipPath id={`${framePrefix}_image_${n.id}`}>
                    <rect width={n.width} height={n.height} />
                  </clipPath>
                  <g clipPath={`url(#${framePrefix}_image_${n.id})`}>
                    {content}
                  </g>
                </>
              );
            return (
              <g
                key={n.id}
                clipPath={
                  n.frameClip
                    ? `url(#${framePrefix}_frame_clip_${n.id})`
                    : undefined
                }
              >
                {n.frameClip && (
                  <defs>
                    <clipPath id={`${framePrefix}_frame_clip_${n.id}`}>
                      <path
                        d={n.frameClip.frame.path}
                        transform={frameTransform(n.frameClip)}
                        clipRule={n.frameClip.frame.fillRule}
                      />
                    </clipPath>
                  </defs>
                )}
                <g clipPath={n.clip ? `url(#item_clip_${n.id})` : undefined}>
                  {n.clip && (
                    <defs>
                      <clipPath id={`item_clip_${n.id}`}>
                        <rect {...n.clip} />
                      </clipPath>
                    </defs>
                  )}
                  <g
                    transform={`translate(${n.x} ${n.y}) rotate(${n.rotation} ${n.width / 2} ${n.height / 2})`}
                    opacity={n.opacity}
                    fill={n.color}
                    data-source-id={n.sourceId}
                    data-region-id={n.flowPlacement?.regionId}
                    onPointerDown={(e) => {
                      if (designer && n.placement === "flow") {
                        e.stopPropagation();
                        onSelect?.(n.sourceId);
                      } else begin(e, n.id);
                    }}
                    style={{
                      cursor: designer
                        ? n.placement === "flow"
                          ? "pointer"
                          : n.locked
                            ? "default"
                            : "move"
                        : undefined,
                    }}
                  >
                    {content}
                    {designer && (
                      <rect
                        width={n.width}
                        height={n.height}
                        fill="transparent"
                        stroke={
                          selectedId === n.id || selectedId === n.sourceId
                            ? "#346df1"
                            : "transparent"
                        }
                        strokeWidth=".75"
                      />
                    )}
                  </g>
                </g>
              </g>
            );
          })}
          {designer && grid && (
            <rect
              width={page.width}
              height={page.height}
              fill="url(#design-grid)"
              pointerEvents="none"
            />
          )}
          {!designer &&
            showProduction &&
            page.bleed &&
            (page.bleed.width > page.trim.width ||
              page.bleed.height > page.trim.height) && (
              <g
                className="proof-bleed-guides"
                pointerEvents="none"
                aria-hidden="true"
              >
                {/* Tint the bleed band and outline the trim so the bleed reads at a glance. */}
                <path
                  fillRule="evenodd"
                  fill="#d6455d"
                  opacity=".12"
                  d={`M${page.bleed.x} ${page.bleed.y}h${page.bleed.width}v${page.bleed.height}h${-page.bleed.width}z M${page.trim.x} ${page.trim.y}v${page.trim.height}h${page.trim.width}v${-page.trim.height}z`}
                />
                <rect
                  x={page.bleed.x}
                  y={page.bleed.y}
                  width={page.bleed.width}
                  height={page.bleed.height}
                  fill="none"
                  stroke="#d6455d"
                  strokeWidth={0.75}
                  vectorEffect="non-scaling-stroke"
                />
                <rect
                  x={page.trim.x}
                  y={page.trim.y}
                  width={page.trim.width}
                  height={page.trim.height}
                  fill="none"
                  stroke="#2f6fd6"
                  strokeWidth={0.75}
                  strokeDasharray="4 3"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            )}
          {designer && (
            <g
              className="proof-selection-boundaries"
              pointerEvents="none"
              aria-hidden="true"
            >
              {page.nodes
                .filter(
                  (n) =>
                    n.id === selectedId ||
                    n.sourceId === selectedId ||
                    selectedGroupMembers?.has(n.sourceId || n.id),
                )
                .map((original) => {
                  const active =
                    drag?.id === original.id ||
                    drag?.memberIds?.has(original.sourceId || original.id)
                      ? drag
                      : undefined;
                  const x =
                    original.x + (active?.mode === "move" ? active.dx : 0);
                  const y =
                    original.y + (active?.mode === "move" ? active.dy : 0);
                  const width =
                    active?.mode === "resize"
                      ? Math.max(1, active.width + active.dx)
                      : original.width;
                  const height =
                    active?.mode === "resize"
                      ? Math.max(1, active.height + active.dy)
                      : original.height;
                  return (
                    <g
                      key={original.id}
                      data-selection-source-id={original.sourceId}
                      transform={`translate(${x} ${y}) rotate(${original.rotation} ${width / 2} ${height / 2})`}
                    >
                      <rect
                        width={width}
                        height={height}
                        fill="none"
                        stroke="white"
                        strokeWidth="4"
                        vectorEffect="non-scaling-stroke"
                      />
                      <rect
                        width={width}
                        height={height}
                        fill="none"
                        stroke="#346df1"
                        strokeWidth="2"
                        vectorEffect="non-scaling-stroke"
                      />
                    </g>
                  );
                })}
            </g>
          )}
          {designer &&
            selected &&
            selected.placement !== "flow" &&
            !selectedMovementGroup && (
              <rect
                x={
                  (drag?.id === selected.id ? drag.x + drag.dx : selected.x) +
                  selected.width -
                  3
                }
                y={
                  (drag?.id === selected.id ? drag.y + drag.dy : selected.y) +
                  selected.height -
                  3
                }
                width="6"
                height="6"
                fill="#346df1"
                stroke="white"
                strokeWidth="1"
                onPointerDown={(e) => begin(e, selected.id, "resize")}
                style={{ cursor: "nwse-resize" }}
              />
            )}
        </svg>
      </div>
    </>
  );
}
